import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { ApiError } from './api';
import { fetchSessionSnapshot, type PairChoice, type SessionSnapshot } from './session-core';
import { haptics } from './haptics';
import { trackAnalytics } from './analytics';

type Choice = PairChoice;
type Interaction = 'same_brain' | 'read_me' | 'dont_match';
type SubmitAnswer = (
  sessionId: string,
  initData: string,
  choice: Choice,
  expectedRevision: number,
  actionId?: string
) => Promise<SessionSnapshot>;

interface AnswerAttempt {
  actionId: string;
  choice: Choice;
  label: string;
  roundId: string;
  roundNumber: number;
  expectedRevision: number;
}

interface ReconcileResult {
  accepted: boolean;
  refreshed: AnswerAttempt | null;
}

const currentRoundFor = (snapshot: SessionSnapshot, interaction: Interaction) => {
  if (interaction === 'read_me') return snapshot.readMe.currentRound;
  if (interaction === 'dont_match') return snapshot.dontMatch.currentRound;
  return snapshot.sameBrain.currentRound;
};

const isSafeAutomaticRetry = (error: unknown): boolean => {
  if (error instanceof ApiError) return error.code === 'REVISION_CONFLICT' || error.code === 'ANSWER_ACK_TIMEOUT';
  // Browser fetch rejects network interruptions with TypeError. The same actionId makes one retry idempotent.
  return error instanceof TypeError;
};

export function useDurablePairAnswer(input: {
  sessionId: string;
  initData: string;
  interaction: Interaction;
  snapshot: SessionSnapshot | null;
  setSnapshot: Dispatch<SetStateAction<SessionSnapshot | null>>;
  submit: SubmitAnswer;
}) {
  const [attempt, setAttempt] = useState<AnswerAttempt | null>(null);
  const [answerBusy, setAnswerBusy] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const round = input.snapshot ? currentRoundFor(input.snapshot, input.interaction) : null;

  useEffect(() => {
    if (!attempt) return;
    if (!round || round.id !== attempt.roundId || round.myAnswer) {
      setAttempt(null);
      setAnswerError(null);
    }
  }, [attempt, round?.id, round?.myAnswer]);

  useEffect(() => {
    setAttempt(null);
    setAnswerError(null);
  }, [input.sessionId]);

  const applySnapshot = (next: SessionSnapshot): void => {
    input.setSnapshot((current) => !current || next.session.revision >= current.session.revision ? next : current);
  };

  const acceptedByAuthority = (next: SessionSnapshot, candidate: AnswerAttempt): boolean => {
    const nextRound = currentRoundFor(next, input.interaction);
    if (!nextRound) return false;
    return nextRound.id !== candidate.roundId || Boolean(nextRound.myAnswer);
  };

  const reconcile = async (candidate: AnswerAttempt): Promise<ReconcileResult> => {
    try {
      const authoritative = await fetchSessionSnapshot(input.sessionId, input.initData);
      applySnapshot(authoritative);
      if (acceptedByAuthority(authoritative, candidate)) {
        setAttempt(null);
        setAnswerError(null);
        haptics.answerLocked();
        return { accepted: true, refreshed: null };
      }
      const refreshed = { ...candidate, expectedRevision: authoritative.session.revision };
      setAttempt(refreshed);
      return { accepted: false, refreshed };
    } catch {
      // Keep the original intent and idempotency key available for an explicit retry.
      return { accepted: false, refreshed: candidate };
    }
  };

  const submitCandidate = async (candidate: AnswerAttempt): Promise<void> => {
    const next = await input.submit(
      input.sessionId,
      input.initData,
      candidate.choice,
      candidate.expectedRevision,
      candidate.actionId
    );
    applySnapshot(next);
    setAttempt(null);
    setAnswerError(null);
    haptics.answerLocked();
  };

  const send = async (candidate: AnswerAttempt): Promise<void> => {
    setAnswerBusy(true);
    setAnswerError(null);
    try {
      await submitCandidate(candidate);
    } catch (error) {
      const reconciled = await reconcile(candidate);
      if (reconciled.accepted) return;

      // A revision race or brief network interruption should be invisible to the human.
      // Retry exactly once, with the same actionId, after resyncing authoritative state.
      if (isSafeAutomaticRetry(error) && reconciled.refreshed) {
        try {
          await submitCandidate(reconciled.refreshed);
          return;
        } catch {
          const secondReconcile = await reconcile(reconciled.refreshed);
          if (secondReconcile.accepted) return;
        }
      }
      setAnswerError(`Couldn't lock ${candidate.label.toUpperCase()}.`);
    } finally {
      setAnswerBusy(false);
    }
  };

  const choose = (choice: Choice, label: string, roundId: string, roundNumber: number, expectedRevision: number): void => {
    if (attempt || answerBusy) return;
    const candidate: AnswerAttempt = {
      actionId: crypto.randomUUID(),
      choice,
      label,
      roundId,
      roundNumber,
      expectedRevision
    };
    haptics.answerTap();
    trackAnalytics('answer_submitted', {
      interactionType: input.interaction,
      choice,
      roundNumber,
      ...(input.snapshot?.session.contextType ? { contextType: input.snapshot.session.contextType } : {})
    });
    setAttempt(candidate);
    void send(candidate);
  };

  const retry = (): void => {
    if (!attempt || answerBusy) return;
    void send(attempt);
  };

  return {
    answerBusy,
    answerError,
    pendingChoice: attempt?.choice ?? null,
    choose,
    retry,
    clearAnswerError: () => setAnswerError(null)
  };
}
